import { AsyncPipe, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  DASHBOARD_USER_TYPE_LABELS,
  DASHBOARD_USER_TYPES,
  DashboardUserType,
  User,
  UserState,
} from '@app/state';
import { AppRoutes, NavLinks } from '@app/core/routing';
import { PLATFORM_LOGO_SRC } from '@app/core/site-context';
import { FeatureFlagDirective } from '@app/core/feature-flags';
import { UiModule } from '@app/ui';
import { HeaderPlatformSearchComponent } from '../header-platform-search/header-platform-search.component';
import { HeaderUserProfileComponent } from '../header-user-profile/header-user-profile.component';
import { HeaderActionsService } from '../header-actions.service';
import { NavMenuItem } from '../header-nav-menu-item.model';
import { NAV_ITEMS } from '../header-nav-items';
import { HeaderNavMenuSectionComponent } from '../header-nav-menu-section/header-nav-menu-section.component';

@Component({
  selector: 'gb-header-platform-menu',
  standalone: true,
  imports: [
    UiModule,
    RouterModule,
    HeaderPlatformSearchComponent,
    HeaderUserProfileComponent,
    HeaderNavMenuSectionComponent,
    AsyncPipe,
    FeatureFlagDirective,
  ],
  templateUrl: './header-platform-menu.component.html',
  styleUrls: ['./header-platform-menu.component.scss'],
})
export class HeaderPlatformMenuComponent implements OnInit {
  protected readonly AppRoutes = AppRoutes;
  protected readonly NavLinks = NavLinks;
  protected readonly platformLogoSrc = PLATFORM_LOGO_SRC;

  @Output() closed = new EventEmitter<void>();

  private readonly store = inject(Store);

  user$: Observable<User> = this.store.select(UserState.user);
  dashboardUserType$: Observable<DashboardUserType> = this.store.select(UserState.effectiveDashboardUserType);

  readonly dashboardUrl = NavLinks.charityDashboard.path;
  readonly dashboardUserTypes = DASHBOARD_USER_TYPES;
  readonly dashboardUserTypeLabels = DASHBOARD_USER_TYPE_LABELS;

  readonly fundraisingItems: NavMenuItem[] = [
    NAV_ITEMS.discover,
    NAV_ITEMS.howItWorks,
    { icon: 'sparkles', label: 'Fundraise with AI', action: () => this.fundraise(), badge: 'New' },
  ];

  readonly charityItems: NavMenuItem[] = [
    { ...NAV_ITEMS.charitySignup, icon: 'landmark' },
    NAV_ITEMS.pricing,
    NAV_ITEMS.gbx,
    {
      icon: 'layout-grid',
      label: NavLinks.charityDashboard.label,
      action: () => this.goToCharityDashboard(),
      external: true,
    },
  ];

  readonly supportItems: NavMenuItem[] = [{ ...NAV_ITEMS.support, icon: 'circle-help' }];

  isUserLoading = false;

  private router = inject(Router);
  private headerActions = inject(HeaderActionsService);

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  ngOnInit(): void {
    this.headerActions.populateUserIfNeeded(isPlatformBrowser(this.platformId), (loading) => {
      this.isUserLoading = loading;
    });
  }

  /** JWT cookie is the source of truth; the store may not be populated yet. */
  get isAuthenticated(): boolean {
    return this.headerActions.isAuthenticated;
  }

  /** Show Log in whenever there is no JWT — including SSR so the CTA is in the HTML. */
  get showGuestCtas(): boolean {
    return !this.headerActions.isAuthenticated;
  }

  close(): void {
    this.closed.emit();
    (document.activeElement as HTMLElement | null)?.blur();
  }

  setDashboardUserType(type: DashboardUserType): void {
    this.close();
    this.headerActions.setDashboardUserType(type);
  }

  logout(): void {
    this.close();
    this.headerActions.logout();
  }

  goToLogin(returnUrl?: string): void {
    this.close();
    this.headerActions.goToLogin(returnUrl);
  }

  goToUserDashboard(): void {
    if (this.isAuthenticated) {
      this.close();
      this.router.navigateByUrl(AppRoutes.dashboard.overview());
      return;
    }
    this.goToLogin(AppRoutes.dashboard.overview());
  }

  goToCharityDashboard(): void {
    this.close();
    this.headerActions.goToCharityDashboard(this.dashboardUrl);
  }

  fundraise(): void {
    this.headerActions.fundraiseWithAi(() => this.close());
  }
}
